import { useMemo, useState, type FormEvent } from 'react';
import { parseQuickAdd, type MatchKind } from '@cleartask/shared';
import { useCurrentUser } from '../../session.js';
import { useLists } from '../lists/useLists.js';
import { normalizeTagName, useTags } from '../tags/useTags.js';
import { newTaskId, useCreateTask } from './useTasks.js';

interface QuickAddProps {
  /**
   * Prazo aplicado quando o texto NAO traz data. Na tela Hoje isso e o fim do
   * dia corrente: uma tarefa criada em "Hoje" que nascesse sem prazo nao
   * apareceria na propria tela onde foi digitada, e o campo pareceria quebrado.
   */
  defaultDueAt: number | null;
  /**
   * Lista aplicada quando o texto nao traz `*Lista`.
   *
   * Vem do filtro ativo: criar uma tarefa enquanto se olha "Regulatório" e um
   * pedido para coloca-la ali. Sem isso, a tarefa nasceria fora da lista que
   * esta na tela e sumiria no mesmo instante.
   */
  defaultListId?: string | null;
  /**
   * Tags aplicadas quando o texto nao traz `#tag`.
   *
   * Mesmo raciocinio do `defaultListId`: criar uma tarefa sob o filtro de uma
   * tag e um pedido para marca-la com ela. Sem isso a tarefa nasceria sem a tag
   * e sumiria da tela no mesmo instante.
   */
  defaultTagIds?: string[];
  placeholder?: string;
}

/** Cor do chip por tipo, para o preview ser lido de relance. */
const ESTILO_DO_CHIP: Record<MatchKind, string> = {
  date: 'bg-brand-blue-soft text-brand-blue',
  time: 'bg-brand-blue-soft text-brand-blue',
  priority: 'bg-surface-2 text-text',
  tag: 'bg-surface-2 text-text',
  list: 'bg-surface-2 text-text',
};

/**
 * Entrada rapida em linguagem natural (spec 10.1).
 *
 * "Enviar OAP amanha 14h !alta #protecao *Regulatorio" vira titulo, prazo,
 * prioridade, tag e lista. A interpretacao acontece a cada tecla e aparece como
 * chips ABAIXO do campo, antes de salvar — a spec exige o preview, e a razao e
 * concreta: sem ele a pessoa so descobre que o parser leu "14h" como parte do
 * titulo depois que a tarefa ja esta criada.
 *
 * O parser mora em `@cleartask/shared` e e puro. Aqui se resolve apenas o que
 * depende do estado do cliente: o nome da lista vira `listId`.
 */
export function QuickAdd({
  defaultDueAt,
  defaultListId = null,
  defaultTagIds = [],
  placeholder = 'Nova tarefa',
}: QuickAddProps) {
  const [texto, setTexto] = useState('');
  const createTask = useCreateTask();
  const user = useCurrentUser();
  const { data: listas } = useLists();
  const { data: tags } = useTags();

  // Reinterpretado a cada tecla. E uma funcao pura sobre uma string curta —
  // memoizar por `texto` ja evita reexecutar quando outro estado muda.
  const parsed = useMemo(
    () => parseQuickAdd(texto, { now: Date.now(), timeZone: user.timezone }),
    [texto, user.timezone],
  );

  /**
   * Lista pela qual o usuario chamou, comparada sem acento e sem caixa.
   *
   * Quem digita "*regulatorio" quer a lista "Regulatório" — exigir a grafia
   * exata transformaria o atalho num quebra-cabeca.
   */
  const listaAlvo = useMemo(() => {
    if (!parsed.listName) return null;

    // `NFD` separa a acentuada em letra + diacritico; o replace remove o
    // diacritico. Aqui o deslocamento de indice nao importa (ao contrario do
    // parser), porque o resultado so e usado para comparar.
    const normalizar = (valor: string) =>
      valor
        .toLocaleLowerCase('pt-BR')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');

    const alvo = normalizar(parsed.listName);
    return (listas?.lists ?? []).find((l) => normalizar(l.name) === alvo) ?? null;
  }, [parsed.listName, listas]);

  /**
   * Resolve os `#nome` do texto para ids de tag existentes.
   *
   * Ate aqui esta era a metade morta da fase 8: o parser reconhecia a tag,
   * mostrava o chip no preview e `tagIds` seguia vazio, porque nao havia rota
   * de tag para consultar. Com `GET /tags`, o nome vira id.
   *
   * Tag inexistente NAO e criada por digitacao, pela mesma razao que vale para
   * `*Lista`: um erro de digitacao viraria uma tag permanente, e o proximo
   * `#proteçao` nao encontraria as tarefas marcadas com `#proteção`. O preview
   * avisa e o painel de tags e onde se cria.
   */
  const { tagsAlvo, tagsDesconhecidas } = useMemo(() => {
    const conhecidas = tags?.tags ?? [];
    const encontradas: string[] = [];
    const faltantes: string[] = [];

    for (const nome of parsed.tagNames) {
      const alvo = normalizeTagName(nome);
      const tag = conhecidas.find((t) => normalizeTagName(t.name) === alvo);
      if (tag) encontradas.push(tag.id);
      else faltantes.push(nome);
    }

    return { tagsAlvo: encontradas, tagsDesconhecidas: faltantes };
  }, [parsed.tagNames, tags]);

  function submit(event: FormEvent) {
    event.preventDefault();
    if (parsed.title === '') return;

    createTask.mutate({
      id: newTaskId(),
      title: parsed.title,
      // A data escrita ganha do padrao da tela: quem digitou "sexta" pediu
      // sexta, nao hoje.
      dueAt: parsed.dueAt ?? defaultDueAt,
      allDay: parsed.dueAt === null ? defaultDueAt !== null : parsed.allDay,
      priority: parsed.priority,
      // O `*Lista` escrito ganha do filtro da tela: quem nomeou a lista no
      // texto foi explicito, e o filtro e so o contexto em que ele digitou.
      listId: listaAlvo?.id ?? defaultListId,
      // As tags escritas ganham do filtro da tela, mesma regra da lista: quem
      // digitou `#tag` foi explicito, e o filtro e so o contexto.
      tagIds: tagsAlvo.length > 0 ? tagsAlvo : defaultTagIds,
    });

    // Limpa na hora, sem esperar a resposta: a escrita e otimista e o campo
    // precisa estar livre para a proxima tarefa.
    setTexto('');
  }

  const listaNaoEncontrada = parsed.listName !== null && listaAlvo === null;
  const temPreview = parsed.matches.length > 0;

  return (
    <form onSubmit={submit} className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <label className="sr-only" htmlFor="quick-add">
          {placeholder}
        </label>
        <input
          id="quick-add"
          // Alvo do atalho `N` (spec 10.2). O `data-` evita acoplar o atalho ao
          // id, que e detalhe de acessibilidade e pode mudar.
          data-quick-add
          value={texto}
          onChange={(event) => setTexto(event.target.value)}
          placeholder={placeholder}
          autoComplete="off"
          className="min-h-touch flex-1 rounded border border-border bg-surface px-3 text-base"
        />
        <button
          type="submit"
          disabled={parsed.title === ''}
          className="min-h-touch shrink-0 rounded bg-brand-blue px-4 font-medium text-white transition-colors duration-150 hover:bg-brand-blue-hover disabled:opacity-40"
        >
          Adicionar
        </button>
      </div>

      {temPreview ? (
        <div
          // `polite`: o preview muda a cada tecla, e interromper a leitura a
          // cada caractere tornaria o campo inutilizavel com leitor de tela.
          aria-live="polite"
          className="flex flex-wrap items-center gap-1.5 text-sm"
        >
          <span className="text-text-mut">Entendi:</span>

          <span className="rounded-sm bg-surface-2 px-2 py-0.5 font-medium">
            {parsed.title === '' ? '(sem título)' : parsed.title}
          </span>

          {parsed.matches.map((match) => (
            <span
              key={`${match.kind}-${match.text}`}
              className={`rounded-sm px-2 py-0.5 ${ESTILO_DO_CHIP[match.kind]}`}
            >
              {match.label}
            </span>
          ))}

          {tagsDesconhecidas.map((nome) => (
            // Sem este aviso volta o defeito original: a pessoa digita
            // `#proteção`, ve o chip no preview e a tarefa nasce sem tag
            // nenhuma.
            <span
              key={nome}
              className="rounded-sm bg-surface-2 px-2 py-0.5 text-text-mut"
            >
              tag “{nome}” não existe — crie em Mais → Tags
            </span>
          ))}

          {listaNaoEncontrada ? (
            // Silenciar isto criaria o pior caso: a pessoa digita "*Regulatorio",
            // ve a lista no preview e a tarefa nasce sem lista nenhuma.
            <span className="rounded-sm bg-surface-2 px-2 py-0.5 text-text-mut">
              lista “{parsed.listName}” não existe — será criada sem lista
            </span>
          ) : null}
        </div>
      ) : null}
    </form>
  );
}
